import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PROVIDER_DETAILS } from './constants';
import useRequest from '../hooks/useRequest';
import envVars from '../constants/envVars';

type IProviderProperties = {
    hero_org_id: string | null;
    provider_name: string | null;
};

// Resolves hero_organization_id -> provider name for cross-provider attribution. Provider
// presentation lives in rtm (not user-status), so we fan out one cached call per org. Each call
// passes hero_organization_id as a one-element ARRAY so it bypasses useRequest's active-org override
// and targets that specific org. Returns a { [orgId]: providerName } map; only fetches when there is
// more than one org (single-tenant users never show attribution).
const useProviderNames = (orgIds: string[]): Record<string, string> => {
    const req = useRequest();
    const enabled = orgIds.length > 1;

    const results = useQueries({
        queries: orgIds.map((orgId) => ({
            // Same key shape as useGetProviderDetails ([PROVIDER_DETAILS, { hero_organization_id }])
            // so the active org's entry is shared and not fetched twice.
            queryKey: [PROVIDER_DETAILS, { hero_organization_id: orgId }],
            enabled,
            queryFn: () =>
                req<IProviderProperties>({
                    url: `${envVars.API_RTM_HOSTNAME}api/enterprise/provider/`,
                    query: { hero_organization_id: [orgId] }
                })
        }))
    });

    return useMemo(() => {
        const map: Record<string, string> = {};
        results.forEach((result, index) => {
            const name = result.data?.provider_name;
            if (name) {
                map[orgIds[index]] = name;
            }
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.map((r) => r.data?.provider_name).join('|'), orgIds.join('|')]);
};

export default useProviderNames;
